'use client';

import SafeHtml from '@/components/common/SafeHtml';
import { useState, useEffect, useRef } from 'react';
import { useParams, useRouter, useSearchParams } from 'next/navigation';
import Image from 'next/image';
import { createClient } from '@/lib/supabase/client';
import { useAuth } from '@/lib/context/AuthContext';
import { useLanguage } from '@/lib/i18n/LanguageContext';
import { formatPrice } from '@/lib/currency/format';
import { normalizeCurrency } from '@/lib/currency/currencies';
import { useToast } from '@/lib/context/ToastContext';
import PaymentCheckout from '@/components/payment/PaymentCheckout';
import GatewayRedirectForm from '@/components/payment/GatewayRedirectForm';
import type { RedirectForm } from '@/lib/payment/types';
import { Tables } from '@/types/database';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { queryKeys } from '@/lib/queries/queryKeys';
import { getHeadlineBprPoints, type BprCreditBonus, type EventType, type SpecialtyPath } from '@/lib/moz-submission-utils';

type Content = Tables<'content'>;
import { Calendar, Clock, MapPin, Award, Users, BookOpen, FileText, Video, Image as ImageIcon, Link as LinkIcon, AlertTriangle, CheckCircle, RefreshCw, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import SimpleAuthModal from '@/components/auth/SimpleAuthModal';
import EventDetailsAutoPrompt from '@/components/details/EventDetailsAutoPrompt';
import TestTaking from '@/components/event/TestTaking';
import SurveyTaking from '@/components/event/SurveyTaking';
import CertificateGenerator from '@/components/certificates/CertificateGenerator';
import PreEventContent from '@/components/event/PreEventContent';
import CertificatesTab from '@/components/event/CertificatesTab';
import EventSpeakersDisplay from '@/components/event/EventSpeakersDisplay';
import EventPartnersDisplay from '@/components/event/EventPartnersDisplay';
import MaterialsDisplay from '@/components/event/MaterialsDisplay';
import TicketSelector from '@/components/event/TicketSelector';
import TicketCheckoutFooter from '@/components/event/TicketCheckoutFooter';
import { Modal } from '@/components/ui/Modal';
import ChangeTicketModal from '@/components/modals/ChangeTicketModal';
import TestNotIncludedNotices from '@/components/tests/TestNotIncludedNotices';
import { useTestNotIncluded } from '@/lib/tests/useTestNotIncluded';
import { posthog } from '@/lib/analytics/posthog';
import { resolveMeetingUrl } from '@/lib/utils/meetingUrlTemplate';
import { analytics } from '@/lib/analytics';
import ShareButton from '@/components/common/ShareButton';
import { upsertRegistrationIntent, updateIntentStep, deleteRegistrationIntent } from '@/lib/registration/intents';
import { useSiteRegistration } from '@/components/registration/useSiteRegistration';
import { isViewerInDifferentTimezone, formatTimeInTimezone, getTimezoneAbbreviation } from '@/lib/utils/date';
import { fetchWithAuthRetry } from '@/lib/utils/fetchWithAuthRetry';
import { classifyPaymentCompleteResponse, schedulePendingPaymentPoll, handlePaymentCompleteClassification, usePollCancelledRef } from '@/lib/payment/pendingCompletion';
import { enrollmentRequiresTest } from '@/lib/utils/ticket-permissions';
import { serverDate } from '@/lib/time/serverClock';

interface ContentBlock {
  id: string;
  type: 'video' | 'text' | 'pdf' | 'image' | 'test' | 'survey' | 'lesson' | 'video_embed' | 'document' | 'text_block';
  data: Record<string, unknown>;
  order: number;
  children?: ContentBlock[];
}

interface Speaker {
  id: string;
  last_name: string | null;
  first_name: string | null;
  middle_name: string | null;
  full_name: string;
  bio: string | null;
  photo_url: string | null;
}

interface Partner {
  id: string;
  name: string;
  logo_url: string | null;
  additional_info: string | null;
}

export default function CourseDetailsPage() {
  const params = useParams();
  const router = useRouter();
  const searchParams = useSearchParams();
  const { user, profile } = useAuth();
  const { t, language } = useLanguage();
  const toast = useToast();
  const queryClient = useQueryClient();
  const [course, setCourse] = useState<Content | null>(null);
  const [contentBlocks, setContentBlocks] = useState<ContentBlock[]>([]);
  const [loading, setLoading] = useState(true);
  const [enrolling, setEnrolling] = useState(false);
  const [enrolled, setEnrolled] = useState(false);
  const [enrollmentId, setEnrollmentId] = useState<string | null>(null);
  const [paymentData, setPaymentData] = useState<{ provider: string; widgetData: Record<string, unknown>; signature?: string; mode?: 'redirect' | 'widget'; redirect?: RedirectForm } | null>(null);
  // The gateway confirmed nothing yet (202) or we couldn't reach it (503) – the
  // webhook/reconciliation cron will settle this shortly. While true, the CTA
  // stays hidden so the participant can't pay a second time.
  const [paymentPending, setPaymentPending] = useState(false);
  // The bounded poll ran out (~60s) without finding the enrollment. Stops the
  // spinner and swaps in a "taking longer than usual" message – it does not
  // mean the payment failed, just that we're no longer actively polling for it.
  const [paymentConfirmDelayed, setPaymentConfirmDelayed] = useState(false);
  const pollCancelledRef = usePollCancelledRef();
  const paymentConfirmingTitleKey = paymentConfirmDelayed ? 'courseDetails.paymentConfirmingDelayedTitle' : 'courseDetails.paymentConfirmingTitle';
  const paymentConfirmingMessageKey = paymentConfirmDelayed ? 'courseDetails.paymentConfirmingDelayed' : 'courseDetails.paymentConfirming';
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [showCertificateModal, setShowCertificateModal] = useState(false);
  const [selectedCertificateType, setSelectedCertificateType] = useState<'participant' | 'speaker'>('participant');
  const [certificateRefreshKey, setCertificateRefreshKey] = useState(0);
  const [shouldRegenerate, setShouldRegenerate] = useState(false);
  const [activeEventTab, setActiveEventTab] = useState<'description' | 'program' | 'speakers' | 'partners' | 'materials' | 'tests' | 'surveys' | 'certificates'>('description');
  const [eventState, setEventState] = useState<'future' | 'live' | 'past'>('future');
  const [hasCertificate, setHasCertificate] = useState(false);
  const [hasCertificatesEnabled, setHasCertificatesEnabled] = useState(false);
  const [checkingCertificate, setCheckingCertificate] = useState(true);
  const [ticketSelections, setTicketSelections] = useState<Array<{ ticketId: string; quantity: number; price: number; name: string }>>([]);
  const [totalTicketPrice, setTotalTicketPrice] = useState<number>(0);
  const [selectedPromocodeId, setSelectedPromocodeId] = useState<string>();
  const [showTicketSelector, setShowTicketSelector] = useState<boolean>(false);
  const [enrollmentTicket, setEnrollmentTicket] = useState<{ id: string; name: string; price: number } | null>(null);
  const [ticketRequiresTest, setTicketRequiresTest] = useState<boolean | null>(null);
  const [enrollmentPaidAmount, setEnrollmentPaidAmount] = useState<number>(0);
  const [enrollmentPaymentStatus, setEnrollmentPaymentStatus] = useState<string | null>(null);
  const [enrollmentRefundStatus, setEnrollmentRefundStatus] = useState<string | null>(null);
  const [cancelModalOpen, setCancelModalOpen] = useState(false);
  const [changeModalOpen, setChangeModalOpen] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [actionError, setActionError] = useState('');
  const [pdfGenerating, setPdfGenerating] = useState(false);
  const supabase = createClient();
  const sectionRefs = useRef<Record<string, HTMLDivElement | null>>({});
  const isScrollingProgrammatically = useRef(false);
  const hasScrolledFromUrl = useRef(false);
  const eventId = params.id as string;
  // The layout redirects a resolvable legacy id before we get here, so a
  // non-uuid at this point is genuinely unknown. Gate every query on it:
  // nine of them used to fire anyway and each returned 400 from Postgres.
  const eventIdIsValid = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(eventId);

  // === TanStack Query: main event data (was a direct Supabase call, now cached + deduplicated) ===
  type CourseDetailsData = { course: Content; contentBlocks: ContentBlock[]; hasCertificatesEnabled: boolean };
  const {
    data: courseDetails,
    isLoading: courseDetailsLoading,
    isError: courseDetailsErrored,
    refetch: refetchCourseDetails,
  } = useQuery<CourseDetailsData | null>({
    queryKey: queryKeys.events.detail(eventId),
    queryFn: async () => {
      // Short-circuit obviously-bad slugs. content.id is a UUID column, so a
      // non-UUID path segment (e.g. /event/compedu) would hit Postgres as a
      // 22P02 cast error and trigger retries with exponential backoff – a
      // multi-second spinner for a user-facing "not found" state.
      const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
      if (!UUID_RE.test(eventId)) return null;

      const { data: courseData, error: courseError } = await supabase
        .from('content')
        .select('*, organizer_profiles(*)')
        .eq('id', eventId)
        .maybeSingle();

      if (courseError) throw courseError;
      // Either the row doesn't exist or RLS hides it (unpublished). Both
      // render the same "not found" UI – the caller gates on `course` being
      // falsy and the `!course` branch handles the rest.
      if (!courseData) return null;

      // The Certificates tab shows when any ticket on the event offers a
      // certificate (allow_certificate is not false), with or without a
      // template: a participant on a template-less ticket still needs the tab
      // to see where their certificate stands.
      const { count: certTicketCount } = await supabase
        .from('tickets')
        .select('id', { count: 'exact', head: true })
        .eq('content_id', eventId)
        .not('allow_certificate', 'is', false);
      const hasTemplates = (certTicketCount ?? 0) > 0;

      const { data: blocks } = await supabase
        .from('content_blocks')
        .select('*')
        .eq('content_id', eventId)
        .order('sort_order');

      const transformedBlocks: ContentBlock[] = (blocks || []).map(block => ({
        id: block.id,
        type: block.type as ContentBlock['type'],
        data: block.block_data as Record<string, unknown>,
        order: block.sort_order || 0,
        children: []
      }));

      // Build hierarchical structure: nest child blocks under their parent lessons
      const rootBlocks: ContentBlock[] = [];
      let currentLesson: ContentBlock | null = null;
      transformedBlocks.forEach(block => {
        const parentId = block.data?.parentId as string | undefined;
        if (block.type === 'lesson') {
          rootBlocks.push(block);
          currentLesson = block;
        } else if (parentId && currentLesson) {
          currentLesson.children = currentLesson.children || [];
          currentLesson.children.push(block);
        } else {
          rootBlocks.push(block);
          currentLesson = null;
        }
      });

      return {
        course: courseData as Content,
        contentBlocks: rootBlocks,
        hasCertificatesEnabled: hasTemplates,
      };
    },
    staleTime: 5 * 60 * 1000,
    enabled: eventIdIsValid,
    // Not-found/RLS-hidden cases resolve cleanly to `null` above, so retries
    // only run on true transient errors. Mobile networks (cell handoffs,
    // captive portals) fail an order of magnitude more often than wired –
    // three tries with backoff keeps the public page loadable on flaky links.
    retry: 3,
    retryDelay: (attempt) => Math.min(1000 * 2 ** attempt, 4000),
    // Override the global refetchOnReconnect=false for this page: a returning
    // mobile user with a fresh connection should auto-recover, not stare at
    // the load-failed screen waiting to tap Retry.
    refetchOnReconnect: 'always',
  });

  // Sync useQuery result to existing state variables (minimal refactor to avoid touching 1700 lines)
  useEffect(() => {
    if (courseDetails) {
      setCourse(courseDetails.course);
      setContentBlocks(courseDetails.contentBlocks);
      setHasCertificatesEnabled(courseDetails.hasCertificatesEnabled);
    }
  }, [courseDetails]);

  // === TanStack Query hooks for event sub-data ===
  const { data: speakers = [] } = useQuery<Speaker[]>({
    queryKey: queryKeys.events.speakers(eventId),
    queryFn: async () => {
      const { data, error } = await supabase
        .from('event_speakers')
        .select(`
          speaker_id,
          sort_order,
          speakers (
            id,
            full_name,
            first_name,
            last_name,
            middle_name,
            bio,
            photo_url
          )
        `)
        .eq('event_id', eventId)
        .order('sort_order', { ascending: true })
        .order('speaker_id', { ascending: true });

      if (error) throw error;
      return (data || [])
        .filter(es => es.speakers)
        .map(es => es.speakers!) as Speaker[];
    },
    staleTime: 30 * 60 * 1000,
    enabled: eventIdIsValid,
  });

  const { data: partners = [] } = useQuery<Partner[]>({
    queryKey: queryKeys.events.partners(eventId),
    queryFn: async () => {
      const { data, error } = await supabase
        .from('event_partners')
        .select('id, name, logo_url, additional_info, sort_order')
        .eq('event_id', eventId)
        .order('sort_order', { ascending: true });

      if (error) throw error;
      return (data || []) as Partner[];
    },
    staleTime: 30 * 60 * 1000,
    enabled: eventIdIsValid,
  });

  type TabCounts = { testCount: number; surveyCount: number; materialCount: number };
  const { data: tabCounts } = useQuery<TabCounts>({
    queryKey: queryKeys.events.tabCounts(eventId),
    queryFn: async () => {
      const [testsResult, surveysResult, materialsResult] = await Promise.all([
        supabase.from('tests').select('id', { count: 'exact', head: true }).eq('content_id', eventId),
        // Must mirror SurveyTaking's filter (enabled + has questions), otherwise
        // the Surveys tab can appear for a survey the participant can never answer.
        supabase.from('surveys').select('id, survey_questions(count)').eq('content_id', eventId).eq('is_active', true),
        // content_materials is organizer-only; the count comes from an RPC.
        supabase.rpc('count_content_materials' as any, { p_content_id: eventId })
      ]);
      const answerableSurveys = (surveysResult.data || []).filter(
        (survey) => ((survey as { survey_questions?: { count: number }[] }).survey_questions?.[0]?.count ?? 0) > 0
      );
      return {
        testCount: testsResult.count || 0,
        surveyCount: answerableSurveys.length,
        materialCount: (materialsResult.data as number | null) ?? 0
      };
    },
    staleTime: 10 * 60 * 1000,
    enabled: eventIdIsValid,
  });
  const testCount = tabCounts?.testCount ?? 0;
  const surveyCount = tabCounts?.surveyCount ?? 0;
  const materialCount = tabCounts?.materialCount ?? 0;

  // Show "test not yet prepared" placeholder when event ended within last 72h and no tests exist
  const showTestPlaceholder = (() => {
    if (!course || testCount > 0 || !enrolled || !enrollmentId) return false;
    if (!course.end_date) return false;
    const endDate = new Date(course.end_date);
    const now = serverDate();
    const hoursSinceEnd = (now.getTime() - endDate.getTime()) / (1000 * 60 * 60);
    return hoursSinceEnd >= 0 && hoursSinceEnd <= 72;
  })();

  // A no-certificate ticket sees the Tests tab only while a test is open, and
  // then only the "not included in your ticket" notice.
  const { data: testNotIncludedItems = [] } = useTestNotIncluded(
    enrolled && eventIdIsValid ? user?.id : null,
    eventId,
  );
  const showTestNotIncluded = ticketRequiresTest === false && testNotIncludedItems.length > 0;
  const showTestsTab = !!(enrolled && enrollmentId) &&
    ((ticketRequiresTest === true && (testCount > 0 || showTestPlaceholder)) || showTestNotIncluded);

  type PricingData = { hasTickets: boolean; currency: string; priceRange: { min: number; max: number } | null };
  const { data: pricingData } = useQuery<PricingData>({
    queryKey: queryKeys.events.pricing(eventId),
    queryFn: async () => {
      const { isWithinWindow } = await import('@/lib/availability/resolveWindow');
      const [{ data, error }, { data: eventRow }] = await Promise.all([
        supabase
          .from('tickets')
          .select('price, currency, requires_promocode, sale_start_date, sale_end_date, sale_start_anchor, sale_end_anchor')
          .eq('content_id', eventId)
          .eq('status', 'available')
          .order('price', { ascending: true }),
        supabase
          .from('content')
          .select('start_date, end_date')
          .eq('id', eventId)
          .single(),
      ]);

      if (!error && data && data.length > 0) {
        const eventTimes = {
          start_date: eventRow?.start_date ?? null,
          end_date: eventRow?.end_date ?? null,
        };
        const publicTickets = data.filter((t) => {
          if (t.requires_promocode) return false;
          return isWithinWindow(
            {
              from: t.sale_start_date,
              from_anchor: t.sale_start_anchor ?? 'absolute',
              until: t.sale_end_date,
              until_anchor: t.sale_end_anchor ?? 'absolute',
            },
            eventTimes
          );
        });
        if (publicTickets.length > 0) {
          const prices = publicTickets.map(t => Number(t.price));
          return { hasTickets: true, currency: normalizeCurrency(publicTickets[0].currency), priceRange: { min: Math.min(...prices), max: Math.max(...prices) } };
        }
        // Tickets exist but none are publicly visible (promo-only or outside sale window).
        // hasTickets must still be true so the ticket selector opens on registration
        // (user can enter a promo code to reveal hidden tickets).
        return { hasTickets: true, currency: normalizeCurrency(data[0].currency), priceRange: null };
      }
      return { hasTickets: false, currency: normalizeCurrency(null), priceRange: null };
    },
    staleTime: 30 * 60 * 1000,
    enabled: eventIdIsValid,
  });
  const hasTickets = pricingData?.hasTickets ?? false;
  const ticketPriceRange = pricingData?.priceRange ?? null;
  const eventCurrency = normalizeCurrency(pricingData?.currency);
  const priceLang: 'uk' | 'en' = language === 'en' ? 'en' : 'uk';

  const { data: mozData } = useQuery<{ academicHours: string | null; eventType: string | null; creditBonus: BprCreditBonus }>({
    queryKey: queryKeys.events.moz(eventId),
    queryFn: async () => {
      const { data: mozResult } = await supabase
        .from('moz_submission_data')
        .select('academic_hours, event_type, intl_accreditation, intl_accreditation_file_url')
        .eq('event_id', eventId)
        .order('updated_at', { ascending: false })
        .limit(1)
        .maybeSingle();

      return {
        academicHours: mozResult?.academic_hours != null ? String(mozResult.academic_hours) : null,
        eventType: mozResult?.event_type || null,
        creditBonus: {
          intlAccreditation: mozResult?.intl_accreditation ?? false,
          intlAccreditationFileUrl: mozResult?.intl_accreditation_file_url ?? null,
        },
      };
    },
    staleTime: 30 * 60 * 1000,
    enabled: eventIdIsValid,
  });
  const mozAcademicHours = mozData?.academicHours ?? null;
  const mozEventType = mozData?.eventType ?? null;

  // Read tab from URL query parameter – wait for data to load and section to render before scrolling
  useEffect(() => {
    if (loading || hasScrolledFromUrl.current) return;
    const tabFromUrl = searchParams.get('tab');
    const validTabs = ['description', 'program', 'speakers', 'partners', 'materials', 'tests', 'surveys', 'certificates'];
    if (tabFromUrl && validTabs.includes(tabFromUrl)) {
      setActiveEventTab(tabFromUrl as 'description' | 'program' | 'speakers' | 'partners' | 'materials' | 'tests' | 'surveys' | 'certificates');
      // Use requestAnimationFrame to wait for the DOM to update after state changes
      requestAnimationFrame(() => {
        const el = sectionRefs.current[tabFromUrl];
        if (el) {
          hasScrolledFromUrl.current = true;
          const offset = 120;
          const elementPos = el.getBoundingClientRect().top + window.scrollY;
          isScrollingProgrammatically.current = true;
          window.scrollTo({ top: elementPos - offset, behavior: 'smooth' });
          setTimeout(() => { isScrollingProgrammatically.current = false; }, 600);
        }
      });
    }
  }, [loading, searchParams, enrolled, enrollmentId, testCount, surveyCount, showTestPlaceholder]);

  // Set loading=false once course details query resolves (success OR error).
  // On error, TanStack Query leaves `data` as undefined; gating on `data` here
  // would deadlock the spinner on not-found/RLS-hidden events.
  useEffect(() => {
    if (!courseDetailsLoading) {
      setLoading(false);
    }
  }, [courseDetailsLoading]);

  // Handle enrollment check when user changes
  useEffect(() => {
    if (!user) {
      setEnrolled(false);
      setEnrollmentId(null);
      setEnrollmentTicket(null);
      setEnrollmentPaidAmount(0);
      setEnrollmentPaymentStatus(null);
      setEnrollmentRefundStatus(null);
      setHasCertificate(false);
      setCheckingCertificate(false);
      return;
    }

    checkEnrollment();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params.id, user]);

  // Calculate event state based on dates
  useEffect(() => {
    if (!course || course.type !== 'event') return;

    const now = serverDate();
    const start = course.start_date ? new Date(course.start_date) : null;
    const end = course.end_date ? new Date(course.end_date) : null;

    if (!start) {
      setEventState('future');
    } else if (now < start) {
      setEventState('future');
    } else if (end && now > end) {
      setEventState('past');
    } else {
      setEventState('live');
    }
  }, [course]);

  // Set default active tab based on available content
  useEffect(() => {
    if (!course || course.type !== 'event') return;

    // Determine which tabs are available
    const availableTabs: Array<'description' | 'program' | 'speakers' | 'partners' | 'materials' | 'tests' | 'surveys' | 'certificates'> = [];

    if (course.description) availableTabs.push('description');
    if (course.program_content) availableTabs.push('program');
    if (speakers.length > 0) availableTabs.push('speakers');
    if (partners.length > 0) availableTabs.push('partners');
    if (enrolled && enrollmentId && materialCount > 0) availableTabs.push('materials');
    if (showTestsTab) availableTabs.push('tests');
    if (enrolled && enrollmentId && surveyCount > 0) availableTabs.push('surveys');
    if (enrolled && enrollmentId && !checkingCertificate && (hasCertificatesEnabled || hasCertificate)) availableTabs.push('certificates');

    // If current active tab is not available, switch to first available tab
    if (availableTabs.length > 0 && !availableTabs.includes(activeEventTab)) {
      setActiveEventTab(availableTabs[0]);
    }
  }, [course, speakers, partners, materialCount, testCount, surveyCount, hasCertificate, hasCertificatesEnabled, checkingCertificate, enrolled, enrollmentId, activeEventTab, showTestsTab]);

  // Scroll-based scrollspy – split into two effects:
  // Effect 1: Stable scroll listener (only depends on course existing)
  const handleScrollRef = useRef<(() => void) | null>(null);

  useEffect(() => {
    if (!course || course.type !== 'event') return;

    const handleScroll = () => {
      if (isScrollingProgrammatically.current) return;

      const scrollTop = window.scrollY + 130; // header(56) + tabbar(~52) + buffer(22)
      const sectionOrder = ['description', 'program', 'speakers', 'partners', 'materials', 'tests', 'surveys', 'certificates'];
      let activeId: string | null = null;

      // Walk sections in order – last one whose top is <= scrollTop wins
      for (const id of sectionOrder) {
        const el = sectionRefs.current[id];
        if (el && el.offsetTop <= scrollTop) {
          activeId = id;
        }
      }

      if (activeId) {
        setActiveEventTab(activeId as typeof activeEventTab);
      }
    };

    handleScrollRef.current = handleScroll;
    window.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll(); // set initial state
    return () => {
      window.removeEventListener('scroll', handleScroll);
      handleScrollRef.current = null;
    };
  }, [course]);

  // Effect 2: Recalculate active tab when sections appear/disappear
  useEffect(() => {
    handleScrollRef.current?.();
  }, [speakers, partners, enrolled, enrollmentId, materialCount, testCount, surveyCount, hasCertificatesEnabled, hasCertificate, checkingCertificate, showTestsTab]);

  // Auto-scroll the active tab button into view in the tab bar (important on mobile)
  // Uses manual horizontal scroll instead of scrollIntoView to avoid scrolling the
  // entire page vertically when the sticky tab bar has unstuck (user scrolled past sections)
  useEffect(() => {
    const tabButton = document.getElementById(`${activeEventTab}-tab`);
    if (tabButton) {
      const tabContainer = tabButton.parentElement;
      if (tabContainer) {
        const containerRect = tabContainer.getBoundingClientRect();
        const buttonRect = tabButton.getBoundingClientRect();
        const scrollLeft = tabContainer.scrollLeft + (buttonRect.left - containerRect.left) - (containerRect.width / 2) + (buttonRect.width / 2);
        tabContainer.scrollTo({ left: scrollLeft, behavior: 'smooth' });
      }
    }
  }, [activeEventTab]);

  // Returns whether an enrollment was found, so the pending-payment poll knows
  // when to stop.
  const checkEnrollment = async (): Promise<boolean> => {
    if (!user) {
      setEnrolled(false);
      setCheckingCertificate(false);
      return false;
    }
    try {
      const { data } = await supabase
        .from('enrollments')
        .select('id, paid_amount, payment_status, refund_status, ticket:tickets (id, name, price)')
        .eq('user_id', user.id)
        .eq('content_id', params.id as string)
        .maybeSingle();

      if (data) {
        setEnrolled(true);
        setEnrollmentId(data.id);
        setEnrollmentPaidAmount(data.paid_amount || 0);
        setEnrollmentPaymentStatus(data.payment_status);
        setEnrollmentRefundStatus(data.refund_status);
        const ticket = data.ticket as unknown as { id: string; name: string; price: number } | null;
        setEnrollmentTicket(ticket || null);
        setTicketRequiresTest(await enrollmentRequiresTest(data.id));
        // Check if certificate exists
        await checkCertificateExists(data.id);
        return true;
      } else {
        setEnrolled(false);
        setEnrollmentId(null);
        setEnrollmentTicket(null);
        setTicketRequiresTest(null);
        setEnrollmentPaidAmount(0);
        setEnrollmentPaymentStatus(null);
        setEnrollmentRefundStatus(null);
        setCheckingCertificate(false);
        return false;
      }
    } catch (error) {
      console.error('Error checking enrollment:', error);
      setEnrolled(false);
      setCheckingCertificate(false);
      return false;
    }
  };

  const checkCertificateExists = async (enrollmentId: string) => {
    try {
      const { data } = await supabase
        .from('certifications')
        .select('id')
        .eq('content_id', params.id as string)
        .eq('enrollment_id', enrollmentId)
        .maybeSingle();

      setHasCertificate(!!data);
    } catch (error) {
      console.error('Error checking certificate:', error);
      setHasCertificate(false);
    } finally {
      setCheckingCertificate(false);
    }
  };

  const canModifyRegistration = () => {
    if (enrollmentRefundStatus === 'completed') return false;
    if (enrollmentPaymentStatus === 'pending') return false;
    if (enrollmentPaymentStatus === 'awaiting_payment') return false;
    if (course?.end_date) {
      const eventEndDate = new Date(course.end_date);
      if (serverDate() >= eventEndDate) return false;
    }
    return true;
  };

  const handleCancelClick = () => {
    setActionError('');
    setCancelModalOpen(true);
  };

  const handleDownloadProgramPdf = async () => {
    if (pdfGenerating) return;
    setPdfGenerating(true);
    try {
      const res = await fetch(`/api/event/${params.id}/program-pdf`, { method: 'POST' });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to generate PDF');

      const jobId = data.job_id;
      const maxAttempts = 30; // 60 seconds with 2s intervals
      for (let i = 0; i < maxAttempts; i++) {
        await new Promise(resolve => setTimeout(resolve, 2000));
        const pollRes = await fetch(`/api/event/${params.id}/program-pdf?job_id=${jobId}`);
        const pollData = await pollRes.json();
        if (pollData.status === 'completed' && pollData.pdf_url) {
          const pdfRes = await fetch(pollData.pdf_url);
          const blob = await pdfRes.blob();
          const blobUrl = URL.createObjectURL(blob);
          const link = document.createElement('a');
          link.href = blobUrl;
          link.download = `program-${params.id}.pdf`;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          URL.revokeObjectURL(blobUrl);
          return;
        }
        if (pollData.status === 'failed') {
          throw new Error(pollData.error_message || 'PDF generation failed');
        }
      }
      throw new Error('PDF generation timed out');
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to generate PDF';
      toast.error(message);
    } finally {
      setPdfGenerating(false);
    }
  };

  const handleCancelConfirm = async () => {
    if (!enrollmentId) return;

    setCancelling(true);
    setActionError('');

    try {
      const response = await fetch(`/api/enrollments/${enrollmentId}/cancel`, {
        method: 'DELETE',
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.errorCode === 'PAID_REGISTRATION' ? t('registrationErrors.PAID_REGISTRATION') : data.error || t('profile.tickets.cancelError'));
      }

      setEnrolled(false);
      setEnrollmentId(null);
      setEnrollmentTicket(null);
      setEnrollmentPaidAmount(0);
      setEnrollmentPaymentStatus(null);
      setEnrollmentRefundStatus(null);
      setCancelModalOpen(false);
    } catch (err) {
      setActionError(err instanceof Error ? err.message : t('profile.tickets.cancelError'));
    } finally {
      setCancelling(false);
    }
  };

  const handleChangeTicketClick = () => {
    setActionError('');
    setChangeModalOpen(true);
  };

  const handleChangeTicketSuccess = () => {
    setChangeModalOpen(false);
    checkEnrollment();
  };

  const registration = useSiteRegistration({
    contentId: course?.id ?? null,
    successKey: 'courseDetails.successfullyEnrolled',
    onFormShown: () => {
      if (user && course) updateIntentStep(user.id, course.id, 'profile_modal_shown');
      setEnrolling(false);
    },
    onRegistered: ({ enrollmentId: newEnrollmentId, offline }) => {
      setEnrolling(false);
      if (!course) return;
      if (!offline) {
        analytics.enrollment.completed({
          content_id: course.id,
          content_type: 'event',
          enrollment_id: newEnrollmentId || '',
          ticket_id: ticketSelections[0]?.ticketId,
          payment_method: 'free',
          total_amount: 0,
          currency: eventCurrency,
          promocode_applied: !!selectedPromocodeId,
        });
      }
      // Delete registration intent - user completed!
      if (user) deleteRegistrationIntent(user.id, course.id);
      setEnrolled(true);
      if (offline) setEnrollmentPaymentStatus('awaiting_payment');
      if (newEnrollmentId) setEnrollmentId(newEnrollmentId);
      // Re-read the enrollment so the ticket-derived state (does this ticket
      // need the test? certificate already issued?) is set too. Without it
      // ticketRequiresTest stayed null and an already-open test had no tab
      // until a reload.
      void checkEnrollment();
      // Invalidate TanStack Query cache so tests/surveys/tabs are fetched fresh
      queryClient.invalidateQueries({ queryKey: queryKeys.events.detail(course.id) });
    },
    onCheckout: (checkout) => {
      if (!course) return;
      analytics.payment.initiated({
        content_id: course.id,
        amount: totalTicketPrice,
        currency: eventCurrency,
        ticket_selections: ticketSelections.map(s => ({
          ticket_id: s.ticketId,
          quantity: s.quantity,
          price: s.price,
        })),
        promocode_applied: !!selectedPromocodeId,
      });
      if (user) updateIntentStep(user.id, course.id, 'payment_started');
      // `enrolling` stays true while the payment widget/redirect is open; its
      // callbacks re-enable the button.
      setPaymentData(checkout);
    },
    onAbort: () => setEnrolling(false),
  });

  const handleEnrollment = async () => {
    if (!user) {
      setShowAuthModal(true);
      return;
    }

    if (!course) return;

    // Track enrollment started
    analytics.enrollment.started({
      content_id: course.id,
      content_type: 'event',
      has_tickets: hasTickets,
      is_paid: ticketPriceRange ? ticketPriceRange.min > 0 : false,
      source_page: 'event_detail',
    });

    // Create registration intent for abandoned cart tracking
    upsertRegistrationIntent(user.id, {
      content_id: course.id,
      content_title: course.title,
      content_type: course.type as 'event' | 'course',
      last_step: 'button_clicked',
    });

    // Check if event has tickets - if so, show ticket selector first
    // If no tickets, proceed with free enrollment (default free ticket behavior)
    if (course.type === 'event' && hasTickets) {
      setShowTicketSelector(true);
      return;
    }

    setEnrolling(true);
    await registration.start({ ticketId: null, promocodeId: null, isFree: true });
  };

  const handleTicketSelectionComplete = async () => {
    if (!user) return;

    // Update intent with ticket selection
    if (course) {
      updateIntentStep(user.id, course.id, 'ticket_selected', {
        ticket_selections: ticketSelections,
        total_price: totalTicketPrice,
      });
    }

    setEnrolling(true);
    await registration.start({
      ticketId: ticketSelections[0]?.ticketId ?? null,
      promocodeId: selectedPromocodeId ?? null,
      isFree: totalTicketPrice === 0,
    });
  };

  const formatDate = (date: string | null, includeWeekday: boolean = true) => {
    if (!date) return t('courseDetails.selfPaced');

    // Always use event timezone when available to avoid browser TZ issues
    // (e.g. Viber in-app WebView reporting wrong timezone)
    if (course?.timezone) {
      const locale = language === 'uk' ? 'uk-UA' : 'en-US';
      const opts: Intl.DateTimeFormatOptions = {
        timeZone: course.timezone,
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        ...(includeWeekday ? { weekday: 'long' } : {}),
      };
      try {
        const formatted = new Intl.DateTimeFormat(locale, opts).format(new Date(date));
        return formatted;
      } catch { /* fall through to browser-TZ fallback */ }
    }

    const dateObj = new Date(date);

    if (language === 'uk') {
      const monthNames = ['січня', 'лютого', 'березня', 'квітня', 'травня', 'червня', 'липня', 'серпня', 'вересня', 'жовтня', 'листопада', 'грудня'];
      const day = dateObj.getDate();
      const month = monthNames[dateObj.getMonth()];
      const year = dateObj.getFullYear();

      if (includeWeekday) {
        const weekdayNames = ['неділя', 'понеділок', 'вівторок', 'середа', 'четвер', "п'ятниця", 'субота'];
        const weekday = weekdayNames[dateObj.getDay()];
        return `${weekday}, ${day} ${month} ${year} р.`;
      } else {
        return `${day} ${month} ${year} р.`;
      }
    }

    if (includeWeekday) {
      return dateObj.toLocaleDateString('en-US', {
        weekday: 'long',
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      });
    } else {
      return dateObj.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      });
    }
  };

  const formatTime = (date: string | null) => {
    if (!date) return null;
    // Always use event timezone when available to avoid browser TZ issues
    if (course?.timezone) {
      return formatTimeInTimezone(date, course.timezone);
    }
    // Fallback: browser timezone (for non-event content without timezone)
    const dateObj = new Date(date);
    const h = dateObj.getHours(), m = dateObj.getMinutes();
    if (h === 0 && m === 0) return null;
    return dateObj.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: false });
  };

  // Dual timezone display helpers – secondary shows viewer's local time
  const showSecondaryTimezone = course ? isViewerInDifferentTimezone(course.timezone) : false;

  const formatViewerLocalTime = (date: string | null) => {
    if (!date) return null;
    const dateObj = new Date(date);
    const h = dateObj.getHours(), m = dateObj.getMinutes();
    if (h === 0 && m === 0) return null;
    const time = dateObj.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: false });
    const browserTz = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const abbr = getTimezoneAbbreviation(browserTz, dateObj);
    return `${time} ${abbr}`;
  };

  const getFormatLabel = (format: string) => {
    switch (format) {
      case 'online': return t('courseDetails.online');
      case 'in_person': return t('courseDetails.inPerson');
      case 'hybrid': return t('courseDetails.hybrid');
      case 'self_paced': return t('courseDetails.selfPaced');
      default: return t('courseDetails.online');
    }
  };

  const getBprPoints = (): string | null => {
    if (!mozAcademicHours || Number(mozAcademicHours) <= 0) return null;
    const hours = parseFloat(mozAcademicHours);
    if (isNaN(hours)) return null;
    const eventFormatValue = course?.event_format || 'online';
    const points = getHeadlineBprPoints((mozEventType || '') as EventType | '', hours, eventFormatValue, mozData?.creditBonus) ?? hours;
    return Number.isInteger(points) ? points.toString() : points.toFixed(1);
  };

  const getPriceDisplay = () => {
    // If event has tickets, use ticket pricing
    if (ticketPriceRange) {
      const { min, max } = ticketPriceRange;
      if (min === max) {
        // Single price point
        if (min === 0) {
          return t('courseDetails.free'); // "БЕЗКОШТОВНО"
        }
        return formatPrice(min, eventCurrency, priceLang);
      } else {
        // Price range
        return `${formatPrice(min, eventCurrency, priceLang)} - ${formatPrice(max, eventCurrency, priceLang)}`;
      }
    }
    // No tickets defined - free event
    return t('courseDetails.free'); // "БЕЗКОШТОВНО"
  };

  const getBlockIcon = (type: string) => {
    switch (type) {
      case 'video':
      case 'video_embed':
        return <Video className="h-5 w-5" />;
      case 'document':
      case 'presentation':
        return <FileText className="h-5 w-5" />;
      case 'text_block':
        return <BookOpen className="h-5 w-5" />;
      case 'image':
        return <ImageIcon className="h-5 w-5" aria-label="Image block" />;
      case 'lesson':
        return <BookOpen className="h-5 w-5" />;
      default:
        return <FileText className="h-5 w-5" />;
    }
  };

  const handleTabClick = (tabName: string) => {
    setActiveEventTab(tabName as 'description' | 'program' | 'speakers' | 'partners' | 'materials' | 'tests' | 'surveys' | 'certificates');
    // Scroll to section
    const el = sectionRefs.current[tabName];
    if (el) {
      const offset = 120; // header(56px) + tabbar(~52px) + buffer(12px)
      const elementPos = el.getBoundingClientRect().top + window.scrollY;
      isScrollingProgrammatically.current = true;
      window.scrollTo({ top: elementPos - offset, behavior: 'smooth' });
      setTimeout(() => { isScrollingProgrammatically.current = false; }, 600);
    }
    // Track tab interaction in PostHog
    posthog?.capture('event_tab_clicked', {
      tab: tabName,
      event_id: course?.id,
      event_name: course?.title,
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (!course) {
    // Distinguish transient fetch errors from a genuine missing/hidden event.
    // Without this branch, a one-off Supabase blip silently renders as
    // "Event not found" with no retry path, which is misleading and forces a
    // full page reload to recover.
    if (courseDetailsErrored) {
      return (
        <div className="min-h-screen bg-gray-50 flex items-center justify-center">
          <div className="text-center">
            <p className="text-gray-600 mb-4 px-4">{t('event.loadFailed')}</p>
            <div className="flex flex-col sm:flex-row gap-2 justify-center items-center">
              <Button onClick={() => refetchCourseDetails()}>
                {t('event.retry')}
              </Button>
              <Button
                variant="link"
                onClick={() => router.push('/')}
                className="p-0"
              >
                {t('courseDetails.returnToHomepage')}
              </Button>
            </div>
          </div>
        </div>
      );
    }
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <p className="text-gray-600 mb-4">{t('event.eventNotFound')}</p>
          <Button
            variant="link"
            onClick={() => router.push('/')}
            className="p-0"
          >
            {t('courseDetails.returnToHomepage')}
          </Button>
        </div>
      </div>
    );
  }

  // Check if event is cancelled
  const isCancelled = !!course.cancelled_at;

  return (
    <div className="bg-white pb-24 lg:pb-0">

      {/* Cancelled Event Banner */}
      {isCancelled && (
        <div className="bg-red-50 border-b border-red-200">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
            <div className="flex items-center gap-3">
              <AlertTriangle className="h-6 w-6 text-red-600 flex-shrink-0" />
              <div>
                <h3 className="font-semibold text-red-800">{t('dashboard.cancellation.eventCancelled')}</h3>
                <p className="text-red-700 text-sm">{t('dashboard.cancellation.publicBannerMessage')}</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Payment Confirming Banner – gateway hasn't confirmed yet (202) or we
          couldn't reach it (503); the webhook/cron will settle it shortly.
          Once the bounded poll gives up (paymentConfirmDelayed), the spinner
          stops and the copy switches to "taking longer than usual" instead of
          implying a poll that's no longer running. */}
      {paymentPending && !enrolled && (
        <div className="bg-blue-50 border-b border-blue-200">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
            <div className="flex items-center gap-3">
              <RefreshCw className={`h-6 w-6 text-blue-600 flex-shrink-0 ${paymentConfirmDelayed ? '' : 'animate-spin'}`} />
              <div>
                <h3 className="font-semibold text-blue-800">{t(paymentConfirmingTitleKey)}</h3>
                <p className="text-blue-700 text-sm">{t(paymentConfirmingMessageKey)}</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Payment: hosted gateway page (default) or on-page widget (PAYMENT_CHECKOUT_MODE=widget) */}
      {paymentData && paymentData.mode === 'redirect' && paymentData.redirect && (
        <GatewayRedirectForm
          redirect={paymentData.redirect}
          message={t('courseDetails.paymentRedirecting')}
          continueLabel={t('courseDetails.paymentContinue')}
          cancelLabel={t('courseDetails.paymentCancel')}
          onCancel={() => { setPaymentData(null); setEnrolling(false); }}
        />
      )}
      {paymentData && !(paymentData.mode === 'redirect' && paymentData.redirect) && (
        <PaymentCheckout
          provider={paymentData.provider}
          widgetData={paymentData.widgetData}
          signature={paymentData.signature}
          onSuccess={async (response) => {
            setPaymentData(null);

            // Call server to verify signature, create enrollment, send email
            try {
              const res = await fetchWithAuthRetry('/api/payment/complete', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(response),
              });

              const result = await res.json();
              const classification = classifyPaymentCompleteResponse(res, result);

              if (classification === 'success') {
                setEnrolled(true);
                if (result.enrollment?.id) {
                  setEnrollmentId(result.enrollment.id);
                }
                void checkEnrollment();
                toast.success(t('courseDetails.paymentSuccessful'));
                analytics.payment.completed({
                  content_id: course.id,
                  enrollment_id: result.enrollment?.id || '',
                  transaction_id: String(paymentData.widgetData.orderReference || paymentData.widgetData.data || ''),
                  amount: totalTicketPrice,
                  currency: eventCurrency,
                });
                analytics.enrollment.completed({
                  content_id: course.id,
                  content_type: 'event',
                  enrollment_id: result.enrollment?.id || '',
                  ticket_id: ticketSelections[0]?.ticketId,
                  payment_method: paymentData.provider,
                  total_amount: totalTicketPrice,
                  currency: eventCurrency,
                  promocode_applied: !!selectedPromocodeId,
                });
              } else {
                // 'pending': gateway hasn't confirmed yet, or we couldn't reach
                // it – the webhook or the reconciliation cron will settle this.
                // Keep the CTA hidden and poll instead of telling the
                // participant their payment failed.
                if (classification === 'failure') {
                  console.error('Payment complete API failed:', result.error);
                }
                handlePaymentCompleteClassification(classification, {
                  toast,
                  t,
                  setPaymentPending,
                  startPendingPoll: () => schedulePendingPaymentPoll({
                    isCancelled: () => pollCancelledRef.current,
                    checkEnrollment,
                    onResolved: () => { setPaymentPending(false); setPaymentConfirmDelayed(false); },
                    onExhausted: () => setPaymentConfirmDelayed(true),
                  }),
                });
              }
            } catch (err) {
              console.error('Payment complete request failed:', err);
              toast.error(t('courseDetails.failedToEnroll'));
            }

            // Delete registration intent
            if (user) {
              deleteRegistrationIntent(user.id, course.id);
            }
            queryClient.invalidateQueries({ queryKey: queryKeys.events.detail(course.id) });
            setEnrolling(false);
          }}
          onError={(error) => {
            analytics.payment.failed({
              content_id: course.id,
              amount: totalTicketPrice,
              error_reason: 'declined',
              retry_count: 0,
            });
            console.error('Payment failed:', error);
            toast.error(t('courseDetails.paymentFailed'));
            setPaymentData(null);
            setEnrolling(false);
          }}
          onClose={() => {
            setPaymentData(null);
            setEnrolling(false);
          }}
        />
      )}

      {registration.element}

      {/* Missing ticket details: asks once per visit */}
      <EventDetailsAutoPrompt contentId={course.id} />

      {/* Auth Modal */}
      <SimpleAuthModal
        isOpen={showAuthModal}
        onClose={() => setShowAuthModal(false)}
        defaultMode="signup"
      />

      {showCertificateModal && (
        <CertificateGenerator
          courseId={course.id}
          courseName={course.title}
          onClose={(dataChanged) => {
            setShowCertificateModal(false);
            setShouldRegenerate(false);
            if (dataChanged) setCertificateRefreshKey(k => k + 1);
          }}
          regenerate={shouldRegenerate}
          certificateType={selectedCertificateType}
          enrollmentId={enrollmentId}
        />
      )}

      {/* Ticket Selector Modal - Bottom sheet on mobile, centered on desktop */}
      {showTicketSelector && course?.type === 'event' && (
        <div
          className="fixed inset-0 bg-black/50 z-50 flex items-end sm:items-center justify-center sm:p-4"
          onClick={(e) => { if (e.target === e.currentTarget) setShowTicketSelector(false); }}
        >
          <div className="bg-white rounded-t-2xl sm:rounded-lg shadow-lg max-w-2xl w-full max-h-[85vh] sm:max-h-[90vh] flex flex-col">
            <div className="flex-shrink-0 bg-white border-b border-gray-200 p-4 sm:p-6 flex items-center justify-between z-10 rounded-t-2xl sm:rounded-t-lg relative">
              {/* Drag handle on mobile */}
              <div className="absolute top-2 left-1/2 -translate-x-1/2 w-10 h-1 bg-gray-300 rounded-full sm:hidden" />
              <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mt-2 sm:mt-0">{t('event.registration.selectYourTickets')}</h2>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setShowTicketSelector(false)}
                className="p-2 -mr-2"
              >
                ✕
              </Button>
            </div>
            <div className="flex-1 overflow-y-auto p-4 sm:p-6">
              <TicketSelector
                eventId={course.id}
                onSelectionChange={(selections, totalPrice, promocodeId) => {
                  setTicketSelections(selections);
                  setTotalTicketPrice(totalPrice);
                  setSelectedPromocodeId(promocodeId);
                }}
                initialPromoCode={new URL(window.location.href).searchParams.get('promo') || undefined}
              />
            </div>
            <TicketCheckoutFooter
              totalPrice={totalTicketPrice}
              currency={eventCurrency}
              hasSelection={ticketSelections.length > 0}
              onConfirm={() => {
                setShowTicketSelector(false);
                handleTicketSelectionComplete();
              }}
            />
          </div>
        </div>
      )}

      {/* Hero Section */}
      <div className="bg-gray-900 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Left: Event Info */}
            <div className="lg:col-span-2">
              <div className="mb-4">
                <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold">{course.title}</h1>
              </div>

              {/* Metadata Grid */}
              <div className="flex flex-col gap-3 text-sm">
                <div className="flex items-center gap-2">
                  <Calendar className="h-4 w-4 text-blue-400" />
                  <div>
                    {course.event_format === 'self_paced'
                      ? <span>{t('courseDetails.startAnytime')}</span>
                      : (() => {
                          const sep = language === 'uk' ? ' ' : ' • ';
                          const isMultiDay = course.end_date && course.start_date?.slice(0, 10) !== course.end_date?.slice(0, 10);
                          if (isMultiDay) {
                            return (
                              <span className="flex flex-col">
                                <span>{formatDate(course.start_date)}{formatTime(course.start_date) ? `${sep}${formatTime(course.start_date)}` : ''}</span>
                                <span>{formatDate(course.end_date)}{formatTime(course.end_date) ? `${sep}${formatTime(course.end_date)}` : ''}</span>
                              </span>
                            );
                          }
                          return <span>{formatDate(course.start_date)}{formatTime(course.start_date) ? `${sep}${formatTime(course.start_date)}${formatTime(course.end_date) ? ` – ${formatTime(course.end_date)}` : ''}` : ''}</span>;
                        })()
                    }
                    {showSecondaryTimezone && course.event_format !== 'self_paced' && formatViewerLocalTime(course.start_date) && (
                      <div className="text-xs text-gray-400">{formatViewerLocalTime(course.start_date)} ({t('courseDetails.yourLocalTime')})</div>
                    )}
                  </div>
                </div>
                <div className="flex items-start gap-2">
                  <MapPin className="h-4 w-4 text-blue-400 mt-0.5 flex-shrink-0" />
                  <span>
                    {course.event_format === 'online' || course.event_format === 'self_paced'
                      ? getFormatLabel(course.event_format || 'online')
                      : (() => {
                          const parts = [course.location, course.location_district, course.location_region, course.location_auditorium].filter(Boolean);
                          const fullLocation = parts.join(', ');
                          if (course.event_format === 'hybrid')
                            return fullLocation ? `${t('courseDetails.online')} + ${fullLocation}` : getFormatLabel('hybrid');
                          if (course.event_format === 'in_person')
                            return fullLocation ? fullLocation : getFormatLabel('in_person');
                          return getFormatLabel(course.event_format || 'online');
                        })()
                    }
                  </span>
                </div>
                {getBprPoints() && (
                  <div className="flex items-center gap-2">
                    <Award className="h-4 w-4 text-blue-400" />
                    <span>{t('courseDetails.cpdCredits')}: {getBprPoints()}</span>
                  </div>
                )}
                {course.show_participant_count !== false && course.enrollment_count !== null && (
                  <div className="flex items-center gap-2">
                    <Users className="h-4 w-4 text-blue-400" />
                    <span className="font-semibold">{course.enrollment_count} {t('event.registered')}</span>
                  </div>
                )}
              </div>

              {/* Price and CTA */}
              <div className="mt-6 flex items-center gap-4 flex-wrap">
                <div className="text-3xl font-bold">{getPriceDisplay()}</div>
                {!enrolled && !isCancelled && !paymentPending && (
                  <>
                    {course.type === 'event' && course.max_participants && course.enrollment_count &&
                      course.enrollment_count >= course.max_participants ? (
                      <div className="bg-gray-500 text-white px-6 py-3 rounded-lg font-semibold">
                        {t('event.soldOut')}
                      </div>
                    ) : (
                      <Button
                        onClick={handleEnrollment}
                        disabled={enrolling}
                        size="lg"
                        className="shadow-lg hover:shadow-xl"
                      >
                        {enrolling
                          ? t('courseDetails.processing')
                          : (course.type === 'event' ? t('courseCard.registerForEvent') : t('courseDetails.enrollNow'))
                        }
                      </Button>
                    )}
                  </>
                )}
              </div>
            </div>

            {/* Right: Event Image */}
            <div className="lg:col-span-1 relative h-64 lg:h-full">
              <Image
                src={course.image_url || '/event-placeholder.svg'}
                alt={course.title}
                fill
                className="object-contain rounded-lg shadow-xl"
                sizes="(max-width: 1024px) 100vw, 33vw"
                priority
              />
            </div>
          </div>
        </div>
      </div>

      {/* Mobile Registration Status Banner – shown at top on mobile for enrolled users */}
      {enrolled && (
        <div className="lg:hidden bg-gradient-to-r from-green-50 to-emerald-50 border-b border-green-200">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4">
            <div className="flex items-center justify-center gap-3">
              <div className="flex-shrink-0 w-9 h-9 bg-green-100 rounded-full flex items-center justify-center">
                <CheckCircle className="h-5 w-5 text-green-600" />
              </div>
              <h3 className="font-bold text-green-900 text-base">
                {course.type === 'event' ? t('courseDetails.youAreRegisteredEvent') : t('courseDetails.youAreEnrolled')}
              </h3>
            </div>
            {enrollmentPaymentStatus === 'awaiting_payment' && (
              <div className="rounded-lg bg-amber-50 border border-amber-200 text-amber-800 text-sm px-4 py-3 mt-3">
                {t('offlinePayment.awaitingBanner') || 'Очікується підтвердження оплати організатором. Дивіться інструкції в листі.'}
              </div>
            )}
            {course.type === 'event' && canModifyRegistration() && (
              <div className="flex flex-col gap-2 mt-3">
                {hasTickets && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={handleChangeTicketClick}
                    className="h-8 px-3 text-blue-600 hover:text-blue-700 hover:bg-blue-50 justify-center"
                  >
                    <RefreshCw className="h-4 w-4 mr-1" />
                    {t('profile.tickets.changeTicket')}
                  </Button>
                )}
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleCancelClick}
                  className="h-8 px-3 text-red-600 hover:text-red-700 hover:bg-red-50 justify-center"
                >
                  <X className="h-4 w-4 mr-1" />
                  {t('profile.tickets.cancelRegistration')}
                </Button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Main Content Area */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Left: Tabs Section */}
            <div className="lg:col-span-2">

              {/* Online Event Access - Only for enrolled users in online or hybrid events */}
              {course.type === 'event' && enrolled && (course.event_format === 'online' || course.event_format === 'hybrid') && eventState !== 'past' && (
                <div className="mb-6">
                  <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border-2 border-blue-200 rounded-lg p-6">
                    <div className="flex items-start gap-4">
                      <Video className="h-6 w-6 text-blue-600 flex-shrink-0 mt-1" />
                      <div className="flex-1">
                        <h3 className="font-bold text-lg text-gray-900 mb-2">
                          {t('event.onlineAccess.title') || 'Join Online Event'}
                        </h3>
                        <p className="text-gray-600 text-sm mb-4">
                          {course.meeting_url
                            ? (t('event.onlineAccess.clickToJoin') || 'Click the button below to join the online event.')
                            : (t('event.onlineAccess.availableBeforeStart') || 'The meeting link will become active shortly before the event starts.')
                          }
                        </p>
                        <div className="relative">
                          <Button
                            variant={course.meeting_url ? 'primary' : 'secondary'}
                            onClick={() => {
                              if (course.meeting_url) {
                                if (enrollmentId) {
                                  fetch('/api/enrollment-tracking', {
                                    method: 'POST',
                                    headers: { 'Content-Type': 'application/json' },
                                    body: JSON.stringify({
                                      enrollment_id: enrollmentId,
                                      milestone: 'started',
                                      activity: 'meeting_url_clicked',
                                    }),
                                  }).catch(err => console.error('Failed to track meeting URL click:', err));
                                }
                                window.open(resolveMeetingUrl(course.meeting_url, profile), '_blank', 'noopener,noreferrer');
                              }
                            }}
                            disabled={!course.meeting_url}
                            className={`group w-full sm:w-auto ${course.meeting_url ? 'shadow-md hover:shadow-lg' : 'cursor-not-allowed'}`}
                          >
                            {course.meeting_url
                              ? (t('event.onlineAccess.joinNow') || 'Join Online Event')
                              : (t('event.onlineAccess.notYetAvailable') || 'Not Yet Available')
                            }
                            {!course.meeting_url && (
                              <div className="hidden group-hover:block absolute left-0 top-full mt-1 text-xs text-gray-600 z-10 whitespace-nowrap pointer-events-none">
                                {t('event.onlineAccess.availableSoon') || 'Available when event starts'}
                              </div>
                            )}
                          </Button>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Pre-Event Content for Future Events */}
              {course.type === 'event' && enrolled && enrollmentId && eventState === 'future' && (
                <PreEventContent
                  eventId={course.id}
                  eventStartDate={course.start_date}
                  enrollmentId={enrollmentId}
                  showTests={ticketRequiresTest === true}
                />
              )}

              {/* Event-specific: Scrollspy tabs + all sections visible */}
              {course.type === 'event' && (
                <div className="mb-8">
                  {/* Sticky Tab Bar */}
                  <div className="sticky top-14 z-40 border-b border-gray-200 bg-white shadow-sm relative">
                    {/* Right fade to indicate more tabs are scrollable */}
                    <div className="absolute right-0 top-0 bottom-0 w-8 bg-gradient-to-l from-white to-transparent pointer-events-none z-10 lg:hidden" />
                    <div className="flex overflow-x-auto scroll-smooth snap-x snap-mandatory scrollbar-none" role="tablist" style={{ scrollbarWidth: 'none' }}>
                      {course.description && (
                        <Button
                          variant="ghost"
                          role="tab"
                          id="description-tab"
                          aria-selected={activeEventTab === 'description'}
                          aria-controls="description-section"
                          onClick={() => handleTabClick('description')}
                          className={`px-6 py-4 font-semibold whitespace-nowrap h-auto rounded-none snap-start ${activeEventTab === 'description'
                              ? 'border-b-2 border-blue-600 text-blue-600'
                              : 'text-gray-600 hover:text-gray-900'
                            }`}
                        >
                          {t('courseDetails.eventDescription') || 'Description'}
                        </Button>
                      )}
                      {course.program_content && (
                        <Button
                          variant="ghost"
                          role="tab"
                          id="program-tab"
                          aria-selected={activeEventTab === 'program'}
                          aria-controls="program-section"
                          onClick={() => handleTabClick('program')}
                          className={`px-6 py-4 font-semibold whitespace-nowrap h-auto rounded-none snap-start ${activeEventTab === 'program'
                              ? 'border-b-2 border-blue-600 text-blue-600'
                              : 'text-gray-600 hover:text-gray-900'
                            }`}
                        >
                          {t('event.tabs.program') || 'Program'}
                        </Button>
                      )}
                      {speakers.length > 0 && (
                        <Button
                          variant="ghost"
                          role="tab"
                          id="speakers-tab"
                          aria-selected={activeEventTab === 'speakers'}
                          aria-controls="speakers-section"
                          onClick={() => handleTabClick('speakers')}
                          className={`px-6 py-4 font-semibold whitespace-nowrap h-auto rounded-none snap-start ${activeEventTab === 'speakers'
                              ? 'border-b-2 border-blue-600 text-blue-600'
                              : 'text-gray-600 hover:text-gray-900'
                            }`}
                        >
                          {t('event.tabs.speakers') || 'Speakers'}
                        </Button>
                      )}
                      {partners.length > 0 && (
                        <Button
                          variant="ghost"
                          role="tab"
                          id="partners-tab"
                          aria-selected={activeEventTab === 'partners'}
                          aria-controls="partners-section"
                          onClick={() => handleTabClick('partners')}
                          className={`px-6 py-4 font-semibold whitespace-nowrap h-auto rounded-none snap-start ${activeEventTab === 'partners'
                              ? 'border-b-2 border-blue-600 text-blue-600'
                              : 'text-gray-600 hover:text-gray-900'
                            }`}
                        >
                          {t('event.tabs.partners') || 'Partners'}
                        </Button>
                      )}
                      {enrolled && enrollmentId && materialCount > 0 && (
                        <Button
                          variant="ghost"
                          role="tab"
                          id="materials-tab"
                          aria-selected={activeEventTab === 'materials'}
                          aria-controls="materials-section"
                          onClick={() => handleTabClick('materials')}
                          className={`px-6 py-4 font-semibold whitespace-nowrap h-auto rounded-none snap-start ${activeEventTab === 'materials'
                              ? 'border-b-2 border-blue-600 text-blue-600'
                              : 'text-gray-600 hover:text-gray-900'
                            }`}
                        >
                          {t('event.tabs.materials') || 'Materials'}
                        </Button>
                      )}
                      {showTestsTab && (
                        <Button
                          variant="ghost"
                          role="tab"
                          id="tests-tab"
                          aria-selected={activeEventTab === 'tests'}
                          aria-controls="tests-section"
                          onClick={() => handleTabClick('tests')}
                          className={`px-6 py-4 font-semibold whitespace-nowrap h-auto rounded-none snap-start ${activeEventTab === 'tests'
                              ? 'border-b-2 border-blue-600 text-blue-600'
                              : 'text-gray-600 hover:text-gray-900'
                            }`}
                        >
                          {t('event.tabs.tests') || 'Tests'}
                        </Button>
                      )}
                      {enrolled && enrollmentId && surveyCount > 0 && (
                        <Button
                          variant="ghost"
                          role="tab"
                          id="surveys-tab"
                          aria-selected={activeEventTab === 'surveys'}
                          aria-controls="surveys-section"
                          onClick={() => handleTabClick('surveys')}
                          className={`px-6 py-4 font-semibold whitespace-nowrap h-auto rounded-none snap-start ${activeEventTab === 'surveys'
                              ? 'border-b-2 border-blue-600 text-blue-600'
                              : 'text-gray-600 hover:text-gray-900'
                            }`}
                        >
                          {t('event.tabs.surveys') || 'Surveys'}
                        </Button>
                      )}
                      {enrolled && enrollmentId && !checkingCertificate && (hasCertificatesEnabled || hasCertificate) && (
                        <Button
                          variant="ghost"
                          role="tab"
                          id="certificates-tab"
                          aria-selected={activeEventTab === 'certificates'}
                          aria-controls="certificates-section"
                          onClick={() => handleTabClick('certificates')}
                          className={`px-6 py-4 font-semibold whitespace-nowrap h-auto rounded-none snap-start ${activeEventTab === 'certificates'
                              ? 'border-b-2 border-blue-600 text-blue-600'
                              : 'text-gray-600 hover:text-gray-900'
                            }`}
                        >
                          {t('event.tabs.certificates') || 'Certificates'}
                        </Button>
                      )}
                    </div>
                  </div>

                  {/* All Sections – visible vertically, scrollspy highlights active tab */}
                  <div className="divide-y divide-gray-200">
                    {course.description && (
                      <div
                        ref={(el) => { sectionRefs.current['description'] = el; }}
                        id="description-section"
                        className="p-6 bg-white scroll-mt-[120px]"
                      >
                        <h2 className="text-2xl font-bold text-gray-900 mb-4">{t('courseDetails.eventDescription') || 'Description'}</h2>
                        <SafeHtml
                          className="text-gray-700 leading-relaxed rich-text-content"
                          html={course.description}
                        />
                      </div>
                    )}
                    {course.show_target_specialties && course.target_specialties &&
                     Array.isArray(course.target_specialties) &&
                     (course.target_specialties as unknown as SpecialtyPath[]).length > 0 && (
                      <div className="p-6 bg-white">
                        <h3 className="text-lg font-semibold text-gray-900 mb-3">
                          {t('courseDetails.targetSpecialties')}
                        </h3>
                        <div className="flex flex-wrap gap-2">
                          {(course.target_specialties as unknown as SpecialtyPath[]).map((sp) => (
                            <span
                              key={sp.path.join(' > ')}
                              className="inline-flex items-center px-3 py-1 bg-indigo-100 text-indigo-800 text-sm rounded-full"
                              title={sp.path.join(' > ')}
                            >
                              {sp.name}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                    {course.program_content && (
                      <div
                        ref={(el) => { sectionRefs.current['program'] = el; }}
                        id="program-section"
                        className="p-6 bg-white scroll-mt-[120px]"
                      >
                        <div className="flex items-center justify-between mb-4">
                          <h2 className="text-2xl font-bold text-gray-900">{t('event.tabs.program') || 'Program'}</h2>
                          {course.allow_pdf_program_download && (
                            <button
                              onClick={handleDownloadProgramPdf}
                              disabled={pdfGenerating}
                              className="inline-flex items-center gap-2 px-3 py-1.5 text-sm font-medium text-blue-700 bg-blue-50 hover:bg-blue-100 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                              {pdfGenerating ? (
                                <div className="w-4 h-4 border-2 border-blue-300 border-t-blue-700 rounded-full animate-spin" />
                              ) : (
                                <FileText className="w-4 h-4" />
                              )}
                              <span>{pdfGenerating ? (language === 'uk' ? 'Генерація PDF...' : 'Generating PDF...') : (language === 'uk' ? 'Завантажити PDF' : 'Download PDF')}</span>
                            </button>
                          )}
                        </div>
                        <SafeHtml
                          className="text-gray-700 leading-relaxed rich-text-content"
                          html={course.program_content}
                        />
                      </div>
                    )}
                    {speakers.length > 0 && (
                      <div
                        ref={(el) => { sectionRefs.current['speakers'] = el; }}
                        id="speakers-section"
                        className="p-6 bg-white scroll-mt-[120px]"
                      >
                        <EventSpeakersDisplay speakers={speakers} />
                      </div>
                    )}
                    {partners.length > 0 && (
                      <div
                        ref={(el) => { sectionRefs.current['partners'] = el; }}
                        id="partners-section"
                        className="p-6 bg-white scroll-mt-[120px]"
                      >
                        <EventPartnersDisplay partners={partners} />
                      </div>
                    )}
                    {/* Shown once the organizer has added a material – never for a
                        promise with nothing uploaded yet. Locked items explain why. */}
                    {enrolled && enrollmentId && materialCount > 0 && (
                      <div
                        ref={(el) => { sectionRefs.current['materials'] = el; }}
                        id="materials-section"
                        className="p-6 bg-white scroll-mt-[120px]"
                      >
                        <h2 className="text-2xl font-bold text-gray-900 mb-4">{t('event.tabs.materials') || 'Materials'}</h2>
                        <MaterialsDisplay
                          // Remount on a ticket or payment change so access is re-read.
                          key={`${enrollmentTicket?.id ?? 'none'}:${enrollmentPaymentStatus ?? ''}`}
                          eventId={course.id}
                          eventEndDate={course.end_date}
                          onUpgradeTicket={handleChangeTicketClick}
                        />
                      </div>
                    )}
                    {showTestsTab && (
                      <div
                        ref={(el) => { sectionRefs.current['tests'] = el; }}
                        id="tests-section"
                        className="p-6 bg-white scroll-mt-[120px]"
                      >
                        <h2 className="text-2xl font-bold text-gray-900 mb-4">{t('event.tabs.tests') || 'Tests'}</h2>
                        {showTestNotIncluded ? (
                          <TestNotIncludedNotices
                            items={testNotIncludedItems}
                            onUpgraded={() => { checkEnrollment(); }}
                          />
                        ) : testCount > 0 ? (
                          <TestTaking
                            eventId={course.id}
                            enrollmentId={enrollmentId}
                            eventStartDate={course.start_date}
                            eventEndDate={course.end_date}
                            contentTitle={course.title}
                            onTestComplete={(result) => {
                              if (result.passed && enrollmentId) {
                                checkCertificateExists(enrollmentId);
                                setCertificateRefreshKey(prev => prev + 1);
                              }
                            }}
                          />
                        ) : (
                          <div className="rounded-xl border border-amber-200 bg-amber-50 p-6 text-center">
                            <Clock className="h-10 w-10 text-amber-400 mx-auto mb-3" />
                            <p className="text-gray-800 font-medium mb-1">
                              {t('event.test.notPreparedByOrganizer')}
                            </p>
                            <p className="text-sm text-gray-600 mb-4">
                              {t('event.test.contactOrganizerAboutTest')}
                            </p>
                            {(course as any).organizer_profiles && (
                              <div className="inline-flex flex-col items-center gap-1 text-sm text-gray-700">
                                <span className="font-medium">{(course as any).organizer_profiles.name}</span>
                                {(course as any).organizer_profiles.email && (
                                  <a href={`mailto:${(course as any).organizer_profiles.email}`} className="text-blue-600 hover:underline">
                                    {(course as any).organizer_profiles.email}
                                  </a>
                                )}
                                {(course as any).organizer_profiles.phone && (
                                  <a href={`tel:${(course as any).organizer_profiles.phone}`} className="text-blue-600 hover:underline">
                                    {(course as any).organizer_profiles.phone}
                                  </a>
                                )}
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    )}
                    {enrolled && enrollmentId && surveyCount > 0 && (
                      <div
                        ref={(el) => { sectionRefs.current['surveys'] = el; }}
                        id="surveys-section"
                        className="p-6 bg-white scroll-mt-[120px]"
                      >
                        <h2 className="text-2xl font-bold text-gray-900 mb-4">{t('event.tabs.surveys') || 'Surveys'}</h2>
                        <SurveyTaking
                          eventId={course.id}
                          enrollmentId={enrollmentId}
                        />
                      </div>
                    )}
                    {enrolled && enrollmentId && !checkingCertificate && (hasCertificatesEnabled || hasCertificate) && (
                      <div
                        ref={(el) => { sectionRefs.current['certificates'] = el; }}
                        id="certificates-section"
                        className="p-6 bg-white scroll-mt-[120px]"
                      >
                        <CertificatesTab
                          eventId={course.id}
                          enrollmentId={enrollmentId}
                          eventState={eventState}
                          eventEndDate={course.end_date}
                          onOpenCertificateModal={(certType, regenerate) => {
                            if (certType) setSelectedCertificateType(certType);
                            setShouldRegenerate(regenerate || false);
                            setShowCertificateModal(true);
                          }}
                          onUpgradeTicket={handleChangeTicketClick}
                          refreshKey={certificateRefreshKey}
                        />
                      </div>
                    )}
                  </div>
                </div>
              )}


              {/* Course Content for non-events */}
              {course.type !== 'event' && (
                <div className="bg-white rounded-lg shadow p-6">
                  <h2 className="text-2xl font-bold mb-6">{t('courseDetails.courseContent')}</h2>

                  {contentBlocks.length > 0 ? (
                    <div className="space-y-4">
                      {contentBlocks.map((block, index) => (
                        <div key={block.id}>
                          {/* Main block (usually a lesson) */}
                          <div className="border rounded-lg p-4 hover:border-blue-500 transition-colors">
                            <div className="flex items-start gap-3">
                              <div className="text-gray-400 mt-1">
                                {getBlockIcon(block.type)}
                              </div>
                              <div className="flex-1">
                                <h3 className="font-semibold text-gray-900">
                                  {index + 1}. {(block.data?.title as string) || `${t('courseDetails.module')} ${index + 1}`}
                                </h3>
                                {typeof block.data?.description === 'string' && block.data.description && (
                                  <p className="text-sm text-gray-600 mt-1">
                                    {block.data.description}
                                  </p>
                                )}
                                {block.type === 'video' && block.data?.duration ? (
                                  <p className="text-sm text-gray-600 mt-1">
                                    <Clock className="inline h-3 w-3 mr-1" />
                                    {block.data.duration as string}
                                  </p>
                                ) : null}
                              </div>
                              {enrolled && (
                                <span className="text-xs bg-green-100 text-green-800 px-2 py-1 rounded">
                                  {t('courseDetails.available')}
                                </span>
                              )}
                            </div>
                          </div>

                          {/* Child blocks (nested under lesson) */}
                          {block.children && block.children.length > 0 && (
                            <div className="ml-8 mt-2 space-y-2">
                              {block.children.map((child, childIndex) => (
                                <div key={child.id} className="border border-gray-200 rounded-lg p-3 hover:border-blue-400 transition-colors bg-gray-50">
                                  <div className="flex items-start gap-3">
                                    <div className="text-gray-400 mt-1">
                                      {getBlockIcon(child.type)}
                                    </div>
                                    <div className="flex-1">
                                      <h4 className="font-medium text-gray-800 text-sm">
                                        {index + 1}.{childIndex + 1} {(child.data?.title as string) || `${t('courseDetails.module')} ${index + 1}.${childIndex + 1}`}
                                      </h4>
                                      {child.type === 'video' && child.data?.duration ? (
                                        <p className="text-xs text-gray-600 mt-1">
                                          <Clock className="inline h-3 w-3 mr-1" />
                                          {child.data.duration as string}
                                        </p>
                                      ) : null}
                                    </div>
                                  </div>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-gray-600">{t('courseDetails.contentAvailableAfterEnrollment')}</p>
                  )}
                </div>
              )}
            </div>

            {/* Sidebar */}
            <div className="space-y-6">
              {/* Registration Status – enrolled users, top of sidebar on desktop */}
              {enrolled && (
                <div className="hidden lg:block bg-gradient-to-br from-green-50 to-emerald-50 rounded-xl p-5 border border-green-200 shadow-sm">
                  <div className="flex items-center gap-3">
                    <div className="flex-shrink-0 w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
                      <CheckCircle className="h-5 w-5 text-green-600" />
                    </div>
                    <div>
                      <h3 className="font-bold text-green-900 text-base">
                        {course.type === 'event' ? t('courseDetails.youAreRegisteredEvent') : t('courseDetails.youAreEnrolled')}
                      </h3>
                    </div>
                  </div>
                  {enrollmentPaymentStatus === 'awaiting_payment' && (
                    <div className="rounded-lg bg-amber-50 border border-amber-200 text-amber-800 text-sm px-4 py-3 mt-3">
                      {t('offlinePayment.awaitingBanner') || 'Очікується підтвердження оплати організатором. Дивіться інструкції в листі.'}
                    </div>
                  )}
                  {course.type === 'event' && canModifyRegistration() && (
                    <div className="flex flex-col gap-2 mt-3 pt-3 border-t border-green-200">
                      {hasTickets && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={handleChangeTicketClick}
                          className="h-8 px-3 text-blue-600 hover:text-blue-700 hover:bg-blue-50 w-full justify-center"
                        >
                          <RefreshCw className="h-4 w-4 mr-1" />
                          {t('profile.tickets.changeTicket')}
                        </Button>
                      )}
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={handleCancelClick}
                        className="h-8 px-3 text-red-600 hover:text-red-700 hover:bg-red-50 w-full justify-center"
                      >
                        <X className="h-4 w-4 mr-1" />
                        {t('profile.tickets.cancelRegistration')}
                      </Button>
                    </div>
                  )}
                </div>
              )}

              {/* Course/Event Details Card */}
              <div className="bg-white rounded-lg shadow p-6">
                <h3 className="font-bold text-lg mb-4">
                  {course.type === 'event' ? t('courseDetails.eventDetails') : t('courseDetails.courseDetails')}
                </h3>
                <dl className="space-y-3">
                  <div>
                    <dt className="text-sm text-gray-600">{t('courseDetails.format')}</dt>
                    <dd className="font-medium">{getFormatLabel(course.event_format || 'online')}</dd>
                  </div>
                  {course.event_format !== 'self_paced' && course.start_date && (
                    <div>
                      <dt className="text-sm text-gray-600">{t('courseDetails.dates')}</dt>
                      <dd className="font-medium">
                        {(() => {
                          const sep = language === 'uk' ? ' ' : ' • ';
                          const isMultiDay = course.end_date && course.start_date?.slice(0, 10) !== course.end_date?.slice(0, 10);
                          if (isMultiDay) {
                            return (
                              <span className="flex flex-col">
                                <span>{formatDate(course.start_date, false)}{formatTime(course.start_date) ? `${sep}${formatTime(course.start_date)}` : ''}</span>
                                <span>{formatDate(course.end_date, false)}{formatTime(course.end_date) ? `${sep}${formatTime(course.end_date)}` : ''}</span>
                              </span>
                            );
                          }
                          return <span>{formatDate(course.start_date, false)}{formatTime(course.start_date) ? `${sep}${formatTime(course.start_date)}${formatTime(course.end_date) ? ` – ${formatTime(course.end_date)}` : ''}` : ''}</span>;
                        })()}
                      </dd>
                      {showSecondaryTimezone && (formatViewerLocalTime(course.start_date) || formatViewerLocalTime(course.end_date)) && (
                        <dd className="text-xs text-gray-500 mt-0.5">
                          {formatViewerLocalTime(course.start_date)}
                          {formatViewerLocalTime(course.end_date) && formatViewerLocalTime(course.start_date) ? ` – ${formatViewerLocalTime(course.end_date)}` : ''}
                          {' '}({t('courseDetails.yourLocalTime')})
                        </dd>
                      )}
                    </div>
                  )}
                  {(course.event_format === 'in_person' || course.event_format === 'hybrid') && (() => {
                    const parts = [course.location, course.location_district, course.location_region, course.location_auditorium].filter(Boolean);
                    return parts.length > 0 ? (
                      <div>
                        <dt className="text-sm text-gray-600">{t('courseDetails.venue')}</dt>
                        <dd className="font-medium">{parts.join(', ')}</dd>
                      </div>
                    ) : null;
                  })()}
                  {getBprPoints() && (
                    <div>
                      <dt className="text-sm text-gray-600">{t('courseDetails.cpdCredits')}</dt>
                      <dd className="font-medium">{getBprPoints()}</dd>
                    </div>
                  )}
                  <div>
                    <dt className="text-sm text-gray-600">{t('courseDetails.price')}</dt>
                    <dd className="font-bold text-xl">
                      {getPriceDisplay()}
                    </dd>
                  </div>
                  {(course as any).organizer_profiles?.name && (
                    <div>
                      <dt className="text-sm text-gray-600">{t('event.organizerInformation') || 'Organizer'}</dt>
                      <dd className="font-medium">{(course as any).organizer_profiles.name}</dd>
                      {(course as any).organizer_profiles.email && (
                        <dd className="text-sm text-gray-600 mt-1">
                          <a href={`mailto:${(course as any).organizer_profiles.email}`} className="text-blue-600 hover:text-blue-700 hover:underline">
                            {(course as any).organizer_profiles.email}
                          </a>
                        </dd>
                      )}
                    </div>
                  )}
                  {course.event_facebook_url && (
                    <div>
                      <dt className="text-sm text-gray-600">
                        <a
                          href={course.event_facebook_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-blue-600 hover:text-blue-700 hover:underline flex items-center gap-1"
                        >
                          <LinkIcon className="h-3 w-3" />
                          Facebook
                        </a>
                      </dt>
                    </div>
                  )}
                  {course.event_website_url && (
                    <div>
                      <dt className="text-sm text-gray-600">
                        <a
                          href={course.event_website_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-blue-600 hover:text-blue-700 hover:underline"
                        >
                          {t('courseDetails.website') || 'Website'}
                        </a>
                      </dt>
                    </div>
                  )}
                </dl>
                {/* Share Button */}
                <div className="pt-4 mt-4 border-t border-gray-200">
                  <ShareButton
                    url={typeof window !== 'undefined' ? `${window.location.origin}/event/${course.id}` : `/event/${course.id}`}
                    title={course.title}
                    text={course.description ? course.description.replace(/<[^>]*>/g, '').slice(0, 100) + '...' : course.title}
                    contentId={course.id}
                    linkTitle={`Event: ${course.title}`}
                    variant="outline"
                    size="md"
                    className="w-full justify-center"
                  />
                </div>
              </div>

              {/* Enrollment Card – only for NOT enrolled (desktop CTA) */}
              {!enrolled && !paymentPending && (
                <div className="bg-blue-50 rounded-lg p-6 border-2 border-blue-200">
                  <h3 className="font-bold text-lg mb-2">{t('courseDetails.readyToStart')}</h3>
                  <p className="text-gray-700 mb-4">
                    {course.type === 'event' ? t('courseDetails.registerForEventToday') : t('courseDetails.joinCourseToday')}
                  </p>
                  {!isCancelled ? (
                    <>
                      {course.type === 'event' && course.max_participants && course.enrollment_count &&
                        course.enrollment_count >= course.max_participants ? (
                        <div className="w-full bg-gray-100 border-2 border-gray-300 px-6 py-3 rounded-lg text-center">
                          <p className="text-gray-600 font-semibold">{t('event.soldOut')}</p>
                        </div>
                      ) : (
                        <Button
                          onClick={handleEnrollment}
                          disabled={enrolling}
                          className="w-full"
                        >
                          {enrolling
                            ? t('courseDetails.processing')
                            : (course.type === 'event' ? t('courseCard.registerForEvent') : t('courseDetails.enrollNow'))
                          }
                        </Button>
                      )}
                    </>
                  ) : null}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Sticky Mobile CTA – hidden when any modal is open so it doesn't cover modal buttons */}
      {!enrolled && !isCancelled && !paymentPending && !showTicketSelector && !registration.isOpen && !showAuthModal && (
        <div className="fixed bottom-0 left-0 right-0 bg-white border-t-2 border-gray-200 shadow-2xl p-4 z-40 lg:hidden">
          <Button
            onClick={handleEnrollment}
            disabled={enrolling}
            className="w-full shadow-lg"
          >
            {enrolling ? t('courseDetails.processing') : (course.type === 'event' ? t('event.register') : t('courseDetails.enrollNow'))}
          </Button>
        </div>
      )}

      {/* Mobile Live Event Join Banner for Enrolled Users */}
      {enrolled && course.type === 'event' && eventState === 'live' && course.live_stream_url && (
        <div className="fixed bottom-0 left-0 right-0 bg-green-600 shadow-2xl p-4 z-50 lg:hidden motion-safe:animate-pulse">
          <div className="flex items-center justify-between gap-3">
            <div className="flex items-center gap-2 text-white">
              <div className="h-3 w-3 rounded-full bg-white motion-safe:animate-pulse" aria-label="Live indicator"></div>
              <span className="font-semibold">{t('event.eventHappeningNow')}</span>
            </div>
            <Button
              variant="secondary"
              onClick={() => {
                if (course.live_stream_url) {
                  if (enrollmentId) {
                    fetch('/api/enrollment-tracking', {
                      method: 'POST',
                      headers: { 'Content-Type': 'application/json' },
                      body: JSON.stringify({
                        enrollment_id: enrollmentId,
                        milestone: 'started',
                        activity: 'stream_clicked',
                      }),
                    }).catch(err => console.error('Failed to track stream click:', err));
                  }
                  try {
                    window.open(course.live_stream_url, '_blank', 'noopener,noreferrer');
                  } catch (error) {
                    console.error('Failed to open streaming URL:', error);
                    toast.error(t('event.streamingError'));
                  }
                }
              }}
              className="bg-white text-green-600 hover:bg-gray-100 whitespace-nowrap shadow-lg"
            >
              {t('event.joinEventNow')}
            </Button>
          </div>
        </div>
      )}

      {/* Cancel Registration Confirmation Modal */}
      <Modal
        isOpen={cancelModalOpen}
        onClose={() => !cancelling && setCancelModalOpen(false)}
        title={t('profile.tickets.cancelConfirmTitle')}
        size="md"
        closeOnBackdropClick={!cancelling}
        closeOnEscape={!cancelling}
      >
        <div className="space-y-4">
          <p className="text-gray-700">
            {t('profile.tickets.cancelConfirmMessage')}
          </p>
          {course && (
            <div className="bg-gray-50 rounded-lg p-4">
              <p className="font-medium text-gray-900">{course.title}</p>
              {enrollmentTicket && (
                <p className="text-sm text-gray-600 mt-1">
                  {t('profile.tickets.ticketName')}: {enrollmentTicket.name}
                </p>
              )}
            </div>
          )}
          {actionError && (
            <p className="text-sm text-red-600">{actionError}</p>
          )}
        </div>
        <Modal.Footer>
          <Button
            variant="secondary"
            className="flex-1"
            onClick={() => setCancelModalOpen(false)}
            disabled={cancelling}
          >
            {t('profile.cancel')}
          </Button>
          <Button
            variant="destructive"
            className="flex-1"
            onClick={handleCancelConfirm}
            disabled={cancelling}
          >
            {cancelling ? t('profile.tickets.cancelling') : t('profile.tickets.cancelConfirmButton')}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Change Ticket Modal */}
      {enrollmentId && course && (
        <ChangeTicketModal
          isOpen={changeModalOpen}
          onClose={() => setChangeModalOpen(false)}
          enrollmentId={enrollmentId}
          contentId={course.id}
          currentTicket={enrollmentTicket}
          paidAmount={enrollmentPaidAmount}
          onSuccess={handleChangeTicketSuccess}
        />
      )}
    </div>
  );
}